// store/shippingWhitelistsSlice/index.slice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiLoadingStatus } from "enum/api-loading-status.enum";
import { IShippingWhitelist } from "models/shippingWhitelist/shippingWhitelist.model";
import { fetchShippingWhitelists } from "services/api/shippingWhitelists";
import { AppThunk, RootState } from "store";

export interface IShippingWhitelistsSlice {
  loadingStatus: ApiLoadingStatus;
  shippingWhitelists: IShippingWhitelist[];
  error: string | null;
}

export const ShippingWhitelistsSliceInitialState: IShippingWhitelistsSlice = {
  loadingStatus: ApiLoadingStatus.IDLE,
  shippingWhitelists: [],
  error: null,
};

export const shippingWhitelistsSlice = createSlice({
  name: "shippingWhitelistsSlice",
  initialState: ShippingWhitelistsSliceInitialState,
  reducers: {
    setData: (
      state,
      action: PayloadAction<IShippingWhitelistsSlice["shippingWhitelists"]>
    ) => {
      state.shippingWhitelists = action.payload;
      state.loadingStatus = ApiLoadingStatus.SUCCESS;
      state.error = null;
    },
    setStatus: (state, action: PayloadAction<ApiLoadingStatus>) => {
      state.loadingStatus = action.payload;
      if (action.payload !== ApiLoadingStatus.LOADING) {
        // Clear error when not loading
        if (action.payload === ApiLoadingStatus.SUCCESS) {
          state.error = null;
        }
      }
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      // Clearing the error (null, as the thunk does when a load starts) is
      // not a failure. It used to flip the status to FAILED for the length of
      // every load, which flashed the Services page's error box.
      if (action.payload) state.loadingStatus = ApiLoadingStatus.FAILED;
    },
    resetState: (state) => {
      state.loadingStatus = ApiLoadingStatus.IDLE;
      state.shippingWhitelists = [];
      state.error = null;
    },
  },
});

export const { setData, setStatus, setError, resetState } = shippingWhitelistsSlice.actions;

export const fetchShippingWhitelistsThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    try {
      dispatch(setStatus(ApiLoadingStatus.LOADING));
      dispatch(setError(null));

      const {
        authSlice: { salesAccountUUID },
      } = getState() as RootState;

      if (!salesAccountUUID) {
        throw new Error('Sales account UUID not found');
      }

      const { data, error } = await fetchShippingWhitelists(salesAccountUUID, true);

      if (error || !data) {
        throw new Error('Failed to fetch shipping whitelists');
      }

      dispatch(setData(data));
    } catch (error) {
      console.error('Error fetching shipping whitelists:', error);
      dispatch(setError(error.message || 'Failed to load services'));
    }
  };
};